import type { Booking, CustomerRecord, Deposit, StudioId } from '@/domain/types';

const HOUR = 60 * 60 * 1000;

// A gap-fill link holds the slot for 24h (board 08). Past that it's a "missed
// deposit" the sweeper chases each morning; past 48h still-unclosed it escalates
// to an Ops report.
export const MISSED_DEPOSIT_MIN_HOURS = 24;
export const MISSED_DEPOSIT_OPS_HOURS = 48;

export interface MissedDepositRow {
  depositId: string;
  customer?: CustomerRecord;
  customerName: string;
  studioId: StudioId;
  amountAud: number;
  /** Whole hours since the link was sent. */
  hoursSinceSent: number;
  /** Session the held link is against, when known. */
  sessionIso?: string;
  /** Unpaid past 48h — escalated to an Ops report. */
  escalated: boolean;
}

function linkedBooking(d: Deposit, bookings: Record<string, Booking>): Booking | undefined {
  if (d.heldBookingId && bookings[d.heldBookingId]) return bookings[d.heldBookingId];
  if (d.bookingId && bookings[d.bookingId]) return bookings[d.bookingId];
  return undefined;
}

/**
 * The sweeper's "missed deposits" morning list (bd tv-d2f, board 08): unpaid
 * gap-fill links (stored status `pending` — sent, not yet paid or closed) whose
 * 24h lock has elapsed. Links unpaid past 48h are flagged as escalated to Ops.
 * Ordered most-overdue first. Network-wide unless scoped by `studioIds`.
 */
export function buildMissedDeposits(args: {
  nowIso: string;
  deposits: Record<string, Deposit>;
  customers: Record<string, CustomerRecord>;
  bookings: Record<string, Booking>;
  studioIds?: StudioId[];
}): MissedDepositRow[] {
  const { nowIso, deposits, customers, bookings, studioIds } = args;
  const now = new Date(nowIso).getTime();
  const allow = studioIds && studioIds.length ? new Set(studioIds) : null;
  const rows: MissedDepositRow[] = [];

  for (const d of Object.values(deposits)) {
    if (allow && !allow.has(d.studioId)) continue;
    // Only unpaid, still-open links — a paid/expired/refunded/rolled-over deposit
    // is already closed out.
    if (d.status !== 'pending') continue;
    const hoursSince = (now - new Date(d.createdAt).getTime()) / HOUR;
    if (hoursSince < MISSED_DEPOSIT_MIN_HOURS) continue; // still inside the 24h lock

    const customer = customers[d.customerId];
    const booking = linkedBooking(d, bookings);
    rows.push({
      depositId: d.id,
      customer,
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : (d.depositReference ?? d.id),
      studioId: d.studioId,
      amountAud: d.amountAud,
      hoursSinceSent: Math.round(hoursSince),
      sessionIso: booking?.start,
      escalated: hoursSince >= MISSED_DEPOSIT_OPS_HOURS,
    });
  }

  return rows.sort((a, b) => b.hoursSinceSent - a.hoursSinceSent);
}
